import { Code2, Globe2, Video, PenLine, Radio, Printer } from "lucide-react";

// Local service detail images (provided by client). Software-development pending — uses Unsplash placeholder for now.
const IMG = {
  software: "/service-software.jpg",
  web: "/service-website.jpg",
  video: "/service-video.jpg",
  content: "/service-content.jpg",
  consulting: "/service-media-consulting.jpg",
  print: "/service-printing.jpg",
};

const PORTFOLIO_IMG = {
  software: "/portfolio-software.png",
  video: "/portfolio-video-production.png",
  web: "/portfolio-web-design.png",
  live: "/portfolio-live-streaming.png",
};

export const services = [
  {
    id: "software-development",
    icon: Code2,
    emoji: "💻",
    title: "Software Development",
    image: IMG.software,
    short: "Robust, scalable apps and platforms tailored to your needs.",
    tagline: "Engineering that earns your customers' trust — every release.",
    long: "From concept to deployment we build modern, secure software that fits your unique workflow. Our engineering team specialises in mobile, web and enterprise platforms with thoughtful architecture and clean, maintainable code.",
    deliverables: [
      "Mobile applications (iOS & Android)",
      "Web applications & SaaS platforms",
      "Enterprise platforms & internal tools",
      "API integration & third-party services",
      "Custom dashboards & analytics",
    ],
    why: "Software is no longer a back-office cost — it's the front door to your business. Customers form their first opinion of your brand within seconds of touching your product. Slow, clunky or insecure software loses revenue silently. We build the opposite: fast, considered systems that scale with your team and stay reliable as your customers multiply.",
    offerings: [
      { title: "Mobile Apps", body: "Native-quality iOS and Android experiences with a single, well-tested codebase. Push notifications, deep links, offline-first patterns, in-app payments — built once, polished everywhere." },
      { title: "Web & SaaS Platforms", body: "Multi-tenant SaaS, dashboards and internal tools designed around clean information architecture, sub-second performance and rock-solid auth. Built on modern stacks your future engineers will love." },
      { title: "Enterprise Integrations", body: "We connect the systems already running your business — CRMs, ERPs, payment gateways, accounting suites — into one fluent operation. No more rekeying data, no more reconciliation nightmares." },
      { title: "AI & Automation", body: "Smart features that quietly remove manual work: document parsing, copilot-style assistants, intelligent search, scheduling agents. Practical AI that earns its keep." },
    ],
    process: [
      { step: "01", title: "Discover", body: "We sit with your team, map every workflow and surface the real problem." },
      { step: "02", title: "Design", body: "Wireframes, prototypes and architecture diagrams reviewed before a line of code is written." },
      { step: "03", title: "Build", body: "Two-week sprints, demoable progress, full test coverage and CI from day one." },
      { step: "04", title: "Launch & Evolve", body: "Cloud-deployed, monitored, and iterated based on real usage." },
    ],
  },
  {
    id: "website-design",
    icon: Globe2,
    emoji: "🌐",
    title: "Website Design",
    image: IMG.web,
    short: "Beautiful, responsive websites built for performance and UX.",
    tagline: "A website that feels as premium as your service actually is.",
    long: "We craft fast, accessible and conversion-focused websites that bring your brand to life. Whether you need a landing page, a content site or a full e-commerce build, we design around your audience and your goals.",
    deliverables: [
      "UI / UX design & prototyping",
      "Responsive web development",
      "Content management systems (CMS)",
      "E-commerce storefronts",
      "SEO & performance optimisation",
    ],
    why: "Most websites are either gorgeous and broken, or functional and forgettable. We refuse the trade-off. Every site we build is engineered for sub-second loads, accessible on every device, and designed with the discipline of an agency that respects how busy your customer is.",
    offerings: [
      { title: "Marketing & Brand Sites", body: "Pixel-precise design, premium motion, considered copy. Sites that pull visitors into your story instead of bouncing them away." },
      { title: "E-commerce Storefronts", body: "Fast, secure online stores on Shopify, WooCommerce or custom stacks. Integrated payments, inventory, analytics and conversion tracking out of the box." },
      { title: "CMS & Editorial Platforms", body: "Bring publishing power to non-technical editors with WordPress, Sanity, Strapi or headless CMS setups. Update copy without ever calling a developer." },
      { title: "Performance & SEO", body: "Audits, Core Web Vitals tuning, structured data and on-page SEO that actually moves your rankings." },
    ],
    process: [
      { step: "01", title: "Brand & Strategy", body: "Audience, message hierarchy, conversion goals." },
      { step: "02", title: "Design Sprint", body: "High-fidelity Figma comps reviewed across desktop, tablet and mobile." },
      { step: "03", title: "Develop & Test", body: "Hand-built front-end, automated checks, real-device QA." },
      { step: "04", title: "Launch & Optimise", body: "Analytics, heatmaps, monthly performance reviews." },
    ],
  },
  {
    id: "video-production",
    icon: Video,
    emoji: "🎥",
    title: "Video Production",
    image: IMG.video,
    short: "Corporate films, adverts, event coverage & live streaming.",
    tagline: "Stories that move people — and budgets.",
    long: "Stories that move people. Our crew handles every stage of production — pre-production planning, on-location filming, post-production editing, and live streaming for events of every scale.",
    deliverables: [
      "Corporate films & brand stories",
      "Television commercials & adverts",
      "Documentaries & feature pieces",
      "Event coverage & multi-cam capture",
      "Live streaming & post-production editing",
    ],
    why: "Video is the closest thing the internet has to eye contact. A good film makes a stranger trust you in 90 seconds. A bad one makes them scroll past forever. Our crew shoots cinema-grade footage, writes scripts that respect attention, and edits with rhythm — so your audience finishes the video and leans in.",
    offerings: [
      { title: "Brand Films", body: "5–90 second cinematic stories that explain who you are and why you matter. Perfect for the top of your homepage." },
      { title: "Commercials & Ads", body: "Punchy, on-message TV and social ads cut for every aspect ratio your media plan needs." },
      { title: "Live Multi-cam Streaming", body: "Conferences, services, weddings, product launches — broadcast in HD across YouTube, Facebook, Zoom and your own platforms simultaneously." },
      { title: "Documentary & Long-form", body: "Long-form storytelling for organisations with stories worth telling at length — investors, communities, alumni." },
    ],
    process: [
      { step: "01", title: "Pre-production", body: "Script, storyboard, location scouting and shot list." },
      { step: "02", title: "Production", body: "Cinema cameras, professional lighting, dedicated audio engineers." },
      { step: "03", title: "Post", body: "Edit, colour grade, sound design, motion graphics, final master." },
      { step: "04", title: "Distribution", body: "Optimised cuts for every platform — YouTube, IG, TikTok, broadcast." },
    ],
  },
  {
    id: "content-creation",
    icon: PenLine,
    emoji: "✍️",
    title: "Content Creation",
    image: IMG.content,
    short: "Engaging content across every digital platform.",
    tagline: "Content that earns attention — not just impressions.",
    long: "Words, visuals and motion that earn attention. We help brands publish consistently and creatively — from social-ready stories to long-form thought leadership and on-brand visual systems.",
    deliverables: [
      "Copywriting & long-form articles",
      "Social media content & campaigns",
      "Blog writing & editorial calendars",
      "Graphic design & motion graphics",
      "Brand identity visuals",
    ],
    why: "Algorithms reward consistency, but audiences only reward quality. We give you both. From scroll-stopping social to long-form articles that rank on Google for years, our content team writes, designs and ships work that compounds — month after month — into real audience equity.",
    offerings: [
      { title: "Editorial & Copywriting", body: "Sales pages, blog series, white papers and brand messaging. Words that sound like your founder on a great day." },
      { title: "Social Content Engines", body: "Monthly content calendars, reels, carousels and short-form video built around what your audience actually shares." },
      { title: "Brand Identity Systems", body: "Logos, type, colour, motion principles and a pattern library your team can use forever — not a one-off PDF that sits in Dropbox." },
      { title: "Motion & Graphic Design", body: "Animated explainers, infographics and pitch decks that turn complex ideas into instantly clear stories." },
    ],
    process: [
      { step: "01", title: "Audit", body: "Current content, voice, audience, competitors." },
      { step: "02", title: "Strategy", body: "Pillars, calendar, channel mix, tone of voice." },
      { step: "03", title: "Produce", body: "Write, design, shoot, animate — in batches." },
      { step: "04", title: "Measure", body: "Monthly review of reach, engagement, leads, conversions." },
    ],
  },
  {
    id: "media-consulting",
    icon: Radio,
    emoji: "📡",
    title: "Media Consulting",
    image: IMG.consulting,
    short: "Strategic digital and media guidance for maximum impact.",
    tagline: "Sharper strategy. Smarter spend. Measurable results.",
    long: "Sharper strategy, smarter spend. We work with leadership teams to clarify positioning, define audiences, choose the right platforms, and measure what actually moves the needle.",
    deliverables: [
      "Digital strategy & roadmaps",
      "Platform advisory & channel mix",
      "Audience analytics & reporting",
      "Brand positioning & messaging",
      "Media training for spokespeople",
    ],
    why: "Most media spend is guesswork dressed in spreadsheets. We replace it with discipline. We sit on your side of the table — define the audience, pick the right channels, set up the analytics that actually answer 'is this working?' and coach your team to handle the spotlight when it lands.",
    offerings: [
      { title: "Strategy & Roadmaps", body: "Quarterly digital and media plans tied directly to your revenue and brand goals — not vanity metrics." },
      { title: "Channel Advisory", body: "Where to be (and where not to be). YouTube vs LinkedIn vs WhatsApp Channels — we'll show you the trade-offs and the numbers." },
      { title: "Analytics & Reporting", body: "GA4, Looker Studio, custom dashboards. Clean data, plain-English insights, and decisions you can actually action." },
      { title: "Media Training", body: "TV, podcast and on-camera coaching for your founders and spokespeople — so the next interview lands the message you intended." },
    ],
    process: [
      { step: "01", title: "Audit", body: "Current spend, channels, positioning, results." },
      { step: "02", title: "Strategy", body: "Quarterly plan with KPIs and a budget that maps to outcomes." },
      { step: "03", title: "Implement", body: "Hands-on or hands-off — we deploy or coach your team." },
      { step: "04", title: "Review", body: "Monthly steering, quarterly retro, annual reset." },
    ],
  },
  {
    id: "printing-publishing",
    icon: Printer,
    emoji: "🖨️",
    title: "Printing & Publishing",
    image: IMG.print,
    short: "High-quality print from branded materials to full publications.",
    tagline: "Premium print, expertly produced. Paper that feels like your brand.",
    long: "Premium print, expertly produced. From everyday business essentials to large-format banners and full publications, we handle design, production and delivery with care.",
    deliverables: [
      "Business cards & stationery",
      "Flyers, brochures & catalogues",
      "Banners & large-format print",
      "Magazines & periodicals",
      "Book publishing & layout",
    ],
    why: "There is a reason why a beautifully printed business card still impresses people in 2026. Tactile work tells your audience you sweat the small stuff. We design, source the right paper stock, manage colour, and ride the press if we have to — so what arrives at your door looks as considered as your digital presence.",
    offerings: [
      { title: "Brand Stationery", body: "Business cards, letterheads, envelopes, ID cards. The everyday print that quietly defines how serious you look." },
      { title: "Marketing Collateral", body: "Flyers, brochures, catalogues, posters — designed and printed in volumes from 50 to 50,000." },
      { title: "Large Format & Signage", body: "Roll-up banners, billboards, vehicle wraps and event signage — print that stops traffic." },
      { title: "Books & Publications", body: "End-to-end book and magazine production: editing, layout, cover design, ISBN registration, printing and distribution support." },
    ],
    process: [
      { step: "01", title: "Brief", body: "Purpose, audience, quantity, paper preference, deadline." },
      { step: "02", title: "Design", body: "Print-ready layouts with proper bleed, margins and Pantone matches." },
      { step: "03", title: "Proof", body: "Hard-copy proofs reviewed before any large run goes to press." },
      { step: "04", title: "Print & Deliver", body: "Quality-checked production, careful packing, delivered to your door." },
    ],
  },
];

export const team = [
  {
    name: "Ven. Alex Uzor",
    role: "CEO / Creative Director",
    photo: "/alex-uzor.png",
    bio: "Visionary leader bridging ICT, ministry and storytelling. Alex sets the strategic direction and creative direction for Digital Archdeacon.",
  },
  {
    name: "Kingsley Cosora",
    role: "Senior Developer",
    photo: "/kingsley-cosora.png",
    bio: "Leads backend and system architecture, building scalable and efficient software solutions.",
  },
  {
    name: "Charity Peters",
    role: "Software Tester",
    photo: "/charity-peters.png",
    bio: "Ensures product quality through rigorous testing, usability checks, and performance validation.",
  },
  {
    name: "Claudius Olayinka",
    role: "Frontend Engineer",
    photo: "/claudius-olayinka.png",
    bio: "Builds responsive, modern user interfaces with attention to detail and user experience.",
  },
  {
    name: "Emmanuel Emeji",
    role: "AI Systems Architect",
    photo: "/emmanuel-emeji.png",
    bio: "Designs intelligent systems and integrates AI solutions into real-world products.",
  },
  {
    name: "Victor Celestine",
    role: "Deployment / DevOps Engineer",
    photo: "/victor-celestine.png",
    bio: "Manages infrastructure, deployment pipelines, and ensures system reliability in production.",
  },
];

export const portfolioCategories = [
  {
    label: "Software Development",
    slug: "software-development",
    description: "Apps, platforms & internal tooling.",
    image: PORTFOLIO_IMG.software,
    case_study: {
      tagline: "From three disconnected tools to one fluent operation.",
      problem:
        "This client — a fast-growing financial services firm — was juggling three disconnected internal systems for client onboarding, KYC verification and transaction reporting. Staff were rekeying data five and six times a day, errors were leaking into compliance reports, and the leadership team had no clean view of pipeline health. The cost of the broken tooling was no longer just frustration — it was real revenue and audit risk.",
      approach:
        "We started with a two-week discovery: shadowing each team, mapping every workflow on a wall, and interrogating the data model behind their existing tools. Then we designed a single unified platform with role-based dashboards, automated KYC checks against third-party APIs, and a real-time reporting layer the executive team could read without filtering a spreadsheet. Engineering moved in two-week sprints with demos every Friday so leadership could course-correct early.",
      result:
        "Onboarding time dropped from 4 days to under 6 hours. Compliance errors fell to near zero in the first quarter. The leadership team now opens one dashboard at the start of every Monday and sees the entire business at a glance — and the firm has scaled to 3× the client count without adding back-office headcount.",
    },
  },
  {
    label: "Video Production",
    slug: "video-production",
    description: "Brand films, ads & live coverage.",
    image: PORTFOLIO_IMG.video,
    case_study: {
      tagline: "A 90-second film that funded an entire campaign.",
      problem:
        "This client was launching a major continental fundraising campaign and had eight weeks to land six-figure pledges from corporate partners. Their existing video assets were a mix of phone footage and stock — warm in intent but visually inconsistent and impossible to lead a boardroom with. They needed one cinematic flagship film that earned trust in 90 seconds and a campaign cut-down for social.",
      approach:
        "We led pre-production with a tight script focused on the campaign's single most powerful story, scouted three locations across two cities, and shot with a cinema package, dedicated audio engineers and a steady-cam operator over three days. In post, we colour-graded for boardroom screens, sound-designed every cue, and produced six different cuts for broadcast, LinkedIn, Instagram, vertical TikTok, a 30-second TV spot, and a 6-second pre-roll.",
      result:
        "The flagship film opened every partner pitch and the campaign hit its funding target three weeks ahead of schedule. The 30-second cut was picked up by two broadcast networks at no media cost, and the social cut crossed half a million views in the first month — turning what was meant to be a single film into a year of compounding brand equity.",
    },
  },
  {
    label: "Web Design",
    slug: "web-design",
    description: "Beautiful, performant websites.",
    image: PORTFOLIO_IMG.web,
    case_study: {
      tagline: "From an outdated theme to a flagship digital storefront.",
      problem:
        "This client — a multi-campus organisation — was running on a Wordpress theme last touched five years prior. Pages took 8 seconds to load on mobile, the donations flow dropped 60% of users at the payment step, and the editorial team had given up trying to publish anything because the CMS was so painful. New visitors were forming a first impression that did not match the quality of the work happening offline.",
      approach:
        "We rebuilt the site from scratch on a modern stack — a headless CMS for the editorial team and a static-rendered front-end for sub-second loads. Information architecture was redesigned around three clear journeys (visit, give, get involved). Donations were rewritten on a payment-provider native integration so the flow dropped from seven steps to two. We trained the editorial team in a single afternoon and shipped the new site behind feature flags, swapping over a quiet weekend.",
      result:
        "Mobile load time dropped from 8.1s to 1.3s. Donation completion rate tripled in the first 30 days. Editorial output went from 1 post per quarter to 8 posts per month — and the brand finally feels online the way it feels in the room.",
    },
  },
  {
    label: "Live Streaming",
    slug: "live-streaming",
    description: "Event broadcast across platforms.",
    image: PORTFOLIO_IMG.live,
    case_study: {
      tagline: "A three-day, four-camera, three-continent broadcast.",
      problem:
        "This client was hosting a three-day pan-African conference with attendees streaming in from over twenty countries — many on patchy connections. Their previous events had relied on a single static camera, audio that distorted under applause, and a YouTube stream that buffered through the keynotes. They needed a broadcast that felt as serious as the speakers on stage.",
      approach:
        "We deployed a four-camera multi-cam rig with dedicated audio engineering, a vision mixer, and a live graphics operator. The signal was simulcast to YouTube, Facebook, LinkedIn and the client's own platform with adaptive bitrates so viewers on slow connections still got a clean feed. We pre-built lower-thirds, agenda overlays, and a virtual control room so the on-site producer could focus on the room while the broadcast quietly handled itself.",
      result:
        "Cumulative live viewership crossed 80,000 across all platforms — five times the previous year — with zero outages across the full three days. Replay packages were edited and published within 24 hours of each session, extending the conference's lifespan into a content library that has continued to drive registrations for the following year.",
    },
  },
];

// Featured case study (shown as a dedicated hero-adjacent section)
export const featuredCaseStudy = {
  label: "Printing & Publishing",
  slug: "printing-publishing",
  kicker: "Featured · Recently shipped",
  image: "/service-printing.jpg",
  case_study: {
    tagline: "A matriarch's life, immortalised on premium paper.",
    short:
      "We designed and printed a Burial Tribute and Photo-Gallery Brochure for the home-going of a beloved matriarch — a bespoke editorial layout, hand-graded photography, and a calibrated press run on heavyweight silk stock. The design was praised for its restraint and warmth; the printing held up to the closest inspection.",
    problem:
      "This client had a short window to deliver something rare — a printed Burial Tribute and Photo-Gallery Brochure for the home-going of a beloved matriarch. The family wanted every page to feel as considered as the woman it honoured: dignified, beautifully laid out, free of the templated funeral-programme look that diminishes most commemorative print. Off-the-shelf shops were either too slow, too rigid in their templates, or simply not capable of the colour fidelity the family expected.",
    approach:
      "We sat with the family early to understand the matriarch's story — the eras, the people, the moments worth foregrounding. Our designers built a bespoke editorial layout drawing on premium magazine-design principles: generous whitespace, considered typography, careful image grading, and a quiet custom monogram woven through the document. Every photograph was retouched and colour-matched by hand. We proofed twice on the exact heavyweight silk stock that would go to press, guaranteeing the brochure felt as considered in the hand as it looked on screen. The job was then run on a calibrated press with full end-to-end colour management.",
    result:
      "The brochure was delivered on schedule, fully bound, and was visibly the most premium piece of print at the service. Family members across three continents asked for additional copies as keepsakes. The design earned praise for its restraint and warmth; the printing — sharpness, colour accuracy, paper feel — held up to the closest inspection. The work has since become our reference standard for tribute publications and other commemorative printing.",
  },
};
